import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { Subscription } from "rxjs";

import {
  ExtractionDecision,
  ExtractionDocument,
  ExtractionItem,
  ExtractionResult,
  PropertyExtractionService,
} from "src/app/core/services/property-extraction.service";
import { PropertyDocumentType } from "src/app/core/services/property-documents.service";

import { AiViewerSource } from "../ai-doc-viewer/ai-doc-viewer.component";
import {
  AiFormPatch,
  buildFormPatch,
  hasValue,
  sameControlValue,
  toDate,
} from "../ai-extraction-patch.util";
import {
  AiAlternativeRow,
  AiCurrentValues,
  AiDocumentRow,
  AiEditorKind,
  AiFieldRow,
  AiFieldSection,
  AiItemRow,
  AiLookups,
  AiOption,
  confidenceLevel,
  confidencePct,
} from "../ai-extraction.models";

export interface AiReviewConfirmEvent {
  decisions: {
    fields: Array<{ id: number; decision: ExtractionDecision; finalValue?: any }>;
    documents: Array<{
      id: number;
      propertyDocumentTypeId?: number | null;
      attachToProperty?: boolean;
    }>;
  };
  patch: AiFormPatch;
}

const SECTIONS: Array<{ key: string; titleKey: string; icon: string }> = [
  { key: "details", titleKey: "ai-extraction.sections.details", icon: "ri-home-4-line" },
  { key: "address", titleKey: "ai-extraction.sections.address", icon: "ri-map-pin-line" },
  { key: "areasNearby", titleKey: "ai-extraction.sections.areas", icon: "ri-ruler-2-line" },
];

const NUMBER_TYPES = ["number", "integer", "money", "area", "year"];

/**
 * Revisão do resultado: o documento de um lado, os valores lidos do outro.
 *
 * Tudo o que o template percorre é pré-calculado em `build()` e atualizado
 * pontualmente: não há getters nem métodos a criar listas nos `*ngFor`.
 */
@Component({
  selector: "app-ai-extraction-review",
  templateUrl: "./ai-extraction-review.component.html",
  styleUrls: ["./ai-extraction-review.component.scss"],
})
export class AiExtractionReviewComponent implements OnChanges, OnDestroy {
  @Input() result: ExtractionResult | null = null;
  @Input() lookups: AiLookups | null = null;
  @Input() currentValues: AiCurrentValues | null = null;
  @Input() documentTypes: PropertyDocumentType[] = [];
  @Input() saving = false;

  @Output() confirmed = new EventEmitter<AiReviewConfirmEvent>();
  @Output() retryDocument = new EventEmitter<number>();
  @Output() closed = new EventEmitter<void>();

  sections: AiFieldSection[] = [];
  divisions: AiItemRow[] = [];
  characteristics: AiItemRow[] = [];
  proximities: AiItemRow[] = [];
  owners: AiItemRow[] = [];
  extras: AiFieldRow[] = [];
  encumbrances: AiItemRow[] = [];
  documents: AiDocumentRow[] = [];

  missingCritical: string[] = [];
  crossIssues: Array<{ code: string }> = [];
  acceptedTotal = 0;
  reviewTotal = 0;
  overallPct: number | null = null;
  chargedCredits = 0;

  activeTab: "fields" | "items" | "owners" | "documents" = "fields";
  selectedKey: string | null = null;

  viewerSource: AiViewerSource | null = null;
  viewerPage: number | null = null;
  viewerSnippet: string | null = null;
  viewerLoading = false;

  private thresholds = { autoAccept: 0.9, review: 0.6 };
  private documentsById = new Map<number, ExtractionDocument>();
  private files = new Map<number, ArrayBuffer>();
  private fileSub?: Subscription;

  constructor(
    private extraction: PropertyExtractionService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["result"] || changes["lookups"] || changes["currentValues"]) {
      this.build();
    }
  }

  ngOnDestroy(): void {
    this.fileSub?.unsubscribe();
  }

  trackRow(_: number, row: { item: ExtractionItem }) {
    return row.item.id;
  }

  trackSection(_: number, section: AiFieldSection) {
    return section.key;
  }

  trackDocument(_: number, document: AiDocumentRow) {
    return document.id;
  }

  // ------------------------------------------------------------ construção

  private build() {
    const result = this.result;
    if (!result) return;

    this.thresholds = result.run.thresholds ?? this.thresholds;
    this.documentsById.clear();
    for (const document of result.run.documents ?? []) {
      this.documentsById.set(document.id, document);
    }

    const fieldRows = result.items
      .filter((item) => item.kind === "FIELD")
      .map((item) => this.toFieldRow(item));

    this.sections = SECTIONS.map((section) => {
      const rows = fieldRows.filter((row) => row.item.payload?.form?.tab === section.key);
      return {
        ...section,
        rows,
        accepted: rows.filter((row) => row.accepted).length,
        collapsed: false,
      };
    }).filter((section) => section.rows.length > 0);

    this.extras = result.items
      .filter((item) => item.kind === "EXTRA")
      .map((item) => this.toFieldRow(item));

    const itemsOf = (kind: string) =>
      result.items.filter((item) => item.kind === kind).map((item) => this.toItemRow(item));
    this.divisions = itemsOf("DIVISION");
    this.characteristics = itemsOf("CHARACTERISTIC");
    this.proximities = itemsOf("PROXIMITY");
    this.owners = itemsOf("OWNER");
    this.encumbrances = itemsOf("ENCUMBRANCE");

    this.documents = (result.run.documents ?? []).map((document) =>
      this.toDocumentRow(document),
    );

    const summary = result.run.summary ?? {};
    this.missingCritical = (summary.missingCriticalFields ?? []).map(
      (key: string) => `ai-extraction.fields.${key}`,
    );
    this.crossIssues = summary.crossIssues ?? [];
    this.overallPct = confidencePct(result.run.overallConfidence);
    this.chargedCredits = result.run.chargedCredits ?? 0;
    this.recount();
  }

  private documentName(item: ExtractionItem): string | null {
    const document = item.documentId ? this.documentsById.get(item.documentId) : null;
    return document?.fileName ?? item.payload?.sourceFileName ?? null;
  }

  private optionsFor(item: ExtractionItem): AiOption[] {
    switch (item.payload?.catalog) {
      case "propertyType":
        return this.lookups?.propertyTypes ?? [];
      case "condition":
        return this.lookups?.conditions ?? [];
      case "energyClass":
        return this.lookups?.energyClasses ?? [];
      default:
        return [];
    }
  }

  private editorFor(item: ExtractionItem): AiEditorKind {
    const catalog = item.payload?.catalog;
    // A localização é revista no separador Morada: aqui só se aceita ou rejeita.
    if (catalog === "location") return "readonly";
    if (catalog) return "catalog";
    if (item.kind === "EXTRA") return "readonly";
    const type = `${item.payload?.type ?? "text"}`;
    if (NUMBER_TYPES.indexOf(type) >= 0) return "number";
    if (type === "date") return "date";
    if (type === "boolean") return "boolean";
    return "text";
  }

  private labelOf(item: ExtractionItem, value: any, options: AiOption[]): string {
    if (!hasValue(value) && typeof value !== "boolean") return "";
    if (item.payload?.catalog === "location") {
      return `${item.payload?.catalogLabel ?? item.value ?? ""}`;
    }
    if (options.length) {
      const option = options.find((entry) => Number(entry.id) === Number(value));
      if (option) return option.label;
    }
    if (typeof value === "boolean") {
      return value ? "ai-extraction.yes" : "ai-extraction.no";
    }
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    return `${value}`;
  }

  private editorValue(item: ExtractionItem, editor: AiEditorKind, raw: any): any {
    if (raw === null || raw === undefined) return null;
    if (editor === "date") return toDate(raw);
    if (editor === "number") {
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (editor === "boolean") return raw === true || raw === "true";
    return raw;
  }

  private currentFormValue(item: ExtractionItem): any {
    const form = item.payload?.form;
    if (!form || !this.currentValues) return null;
    const tab = form.tab as keyof AiCurrentValues;
    return this.currentValues[tab]?.[form.control] ?? null;
  }

  private toFieldRow(item: ExtractionItem): AiFieldRow {
    const editor = this.editorFor(item);
    const options = this.optionsFor(item);
    const readRaw = item.payload?.patchValue ?? null;
    const finalRaw =
      item.finalValue && typeof item.finalValue === "object" && "value" in item.finalValue
        ? item.finalValue.value
        : readRaw;

    const readValue = this.editorValue(item, editor, readRaw);
    const value = this.editorValue(item, editor, finalRaw);
    const current = this.currentFormValue(item);
    const compare = item.key === "yearOfConstruction" && value ? new Date(`${value}-01-01T00:00:00`) : value;
    const overwrites =
      item.kind === "FIELD" && hasValue(current) && !sameControlValue(current, compare);

    let accepted = item.decision === "ACCEPTED" || item.decision === "EDITED";
    // Um valor que o utilizador já escreveu não é substituído sem ele o dizer.
    if (overwrites && !item.decidedByUser) accepted = false;
    // Campo de catálogo sem correspondência não pode ser aceite tal como está.
    if (editor === "catalog" && !hasValue(value)) accepted = false;
    if (item.kind === "EXTRA") accepted = false;

    const alternatives: AiAlternativeRow[] = (item.alternatives ?? []).map((alternative) => {
      const alternativeValue = this.editorValue(
        item,
        editor,
        alternative.payload?.patchValue ?? null,
      );
      return {
        id: alternative.id,
        value: alternativeValue,
        label:
          this.labelOf(item, alternativeValue, options) ||
          `${alternative.payload?.catalogLabel ?? alternative.value ?? ""}`,
        documentId: alternative.documentId,
        documentName: this.documentName(alternative),
        page: alternative.page,
        snippet: alternative.snippet,
        confidencePct: confidencePct(alternative.confidence),
        conflicting: !!alternative.payload?.conflicting,
      };
    });

    return {
      item,
      labelKey: `ai-extraction.fields.${item.key}`,
      editor,
      options,
      value,
      readValue,
      readLabel: this.labelOf(item, readValue, options) || `${item.value ?? ""}`,
      unit: item.unit ?? item.payload?.unit ?? null,
      accepted,
      edited: item.decision === "EDITED",
      confidenceLevel: confidenceLevel(item.confidence, this.thresholds),
      confidencePct: confidencePct(item.confidence),
      documentName: this.documentName(item),
      issues: (item.payload?.issues ?? []).map(
        (code: string) => `ai-extraction.issues.${code}`,
      ),
      overwrites,
      currentLabel: overwrites ? this.labelOf(item, current, options) : null,
      alternatives,
      showAlternatives: alternatives.some((alternative) => alternative.conflicting),
    };
  }

  private toItemRow(item: ExtractionItem): AiItemRow {
    const source =
      item.finalValue && typeof item.finalValue === "object" && !("value" in item.finalValue)
        ? item.finalValue
        : item.payload ?? {};
    const data = JSON.parse(JSON.stringify(source ?? {}));
    const row: AiItemRow = {
      item,
      data,
      accepted: item.decision === "ACCEPTED" || item.decision === "EDITED",
      confidenceLevel: confidenceLevel(item.confidence, this.thresholds),
      confidencePct: confidencePct(item.confidence),
      documentName: this.documentName(item),
      blocked: false,
      blockedKey: null,
      areasText: (Array.isArray(data?.individualAreas) ? data.individualAreas : []).join(" · "),
    };
    this.validateItem(row);
    if (row.blocked) row.accepted = false;
    return row;
  }

  private toDocumentRow(document: ExtractionDocument): AiDocumentRow {
    const failed = document.status === "FAILED";
    return {
      id: document.id,
      fileName: document.fileName,
      typeKey: `ai-extraction.doc-types.${document.detectedType ?? "OUTRO"}`,
      status: document.status,
      readable: document.readable,
      failed,
      processing: document.status === "PROCESSING",
      summary: document.summary,
      error: document.errorMessage,
      propertyDocumentTypeId: document.propertyDocumentTypeId,
      attachToProperty: document.attachToProperty,
      canAttach: !document.fromProperty && document.detectedType !== "FOTOGRAFIA",
      billedCredits: document.billedCredits,
      isHandwritten: document.isHandwritten,
      dirty: false,
    };
  }

  /** O que falta a um item para poder ser aplicado ao formulário. */
  private validateItem(row: AiItemRow) {
    const data = row.data ?? {};
    let blockedKey: string | null = null;

    switch (row.item.kind) {
      case "DIVISION":
        if (!hasValue(data.divisionId)) blockedKey = "ai-extraction.blocked.division";
        else if (!(Number(data.count) > 0)) blockedKey = "ai-extraction.blocked.count";
        break;
      case "CHARACTERISTIC":
        if (!hasValue(data.characteristicId)) {
          blockedKey = "ai-extraction.blocked.characteristic";
        }
        break;
      case "PROXIMITY":
        if (!hasValue(data.nearbyId)) blockedKey = "ai-extraction.blocked.nearby";
        else if (!hasValue(data.unitId)) blockedKey = "ai-extraction.blocked.unit";
        break;
      case "OWNER": {
        const words = `${data.fullName ?? ""}`.trim().split(/\s+/).filter(Boolean);
        const linked = hasValue(data.selectedContactId);
        if (!linked && words.length < 2) blockedKey = "ai-extraction.blocked.owner-name";
        else if (!linked && !hasValue(data.email) && !hasValue(data.phone)) {
          blockedKey = "ai-extraction.blocked.owner-contact";
        }
        break;
      }
      case "ENCUMBRANCE":
        blockedKey = null;
        break;
    }

    row.blockedKey = blockedKey;
    row.blocked = !!blockedKey;
  }

  private recount() {
    for (const section of this.sections) {
      section.accepted = section.rows.filter((row) => row.accepted).length;
    }
    const itemRows = this.divisions
      .concat(this.characteristics)
      .concat(this.proximities)
      .concat(this.owners);
    const fieldRows = this.sections.reduce(
      (list, section) => list.concat(section.rows),
      [] as AiFieldRow[],
    );
    this.acceptedTotal =
      fieldRows.filter((row) => row.accepted).length +
      itemRows.filter((row) => row.accepted).length;
    this.reviewTotal = fieldRows.length + itemRows.length;
  }

  // ------------------------------------------------------------ interação

  selectTab(tab: "fields" | "items" | "owners" | "documents") {
    this.activeTab = tab;
  }

  toggleSection(section: AiFieldSection) {
    section.collapsed = !section.collapsed;
  }

  setSection(section: AiFieldSection, accepted: boolean) {
    for (const row of section.rows) this.setAccepted(row, accepted);
    this.recount();
  }

  setAll(accepted: boolean) {
    for (const section of this.sections) {
      for (const row of section.rows) this.setAccepted(row, accepted);
    }
    const itemRows = this.divisions
      .concat(this.characteristics)
      .concat(this.proximities)
      .concat(this.owners);
    for (const row of itemRows) row.accepted = accepted && !row.blocked;
    this.recount();
  }

  private setAccepted(row: AiFieldRow, accepted: boolean) {
    const canAccept = row.editor === "readonly" ? hasValue(row.value) : this.hasEditorValue(row);
    row.accepted = accepted && canAccept;
  }

  private hasEditorValue(row: AiFieldRow): boolean {
    if (row.editor === "boolean") return row.value === true || row.value === false;
    return hasValue(row.value);
  }

  toggleField(row: AiFieldRow) {
    this.setAccepted(row, !row.accepted);
    this.recount();
  }

  /** O utilizador mexeu no valor: passa a ser o valor final e fica aceite. */
  onFieldEdited(row: AiFieldRow) {
    row.edited = !this.sameValue(row.value, row.readValue);
    row.accepted = this.hasEditorValue(row);
    this.recount();
  }

  restoreField(row: AiFieldRow) {
    row.value = row.readValue;
    row.edited = false;
    row.accepted = this.hasEditorValue(row);
    this.recount();
  }

  /**
   * Usa o valor de outro documento. As alternativas ficam todas na lista, por
   * isso a troca pode ser desfeita escolhendo outra ou restaurando o valor lido.
   */
  useAlternative(row: AiFieldRow, alternative: AiAlternativeRow) {
    row.value = alternative.value;
    row.edited = !this.sameValue(row.value, row.readValue);
    row.accepted = this.hasEditorValue(row);
    this.showEvidence(
      `${row.item.id}`,
      alternative.documentId,
      alternative.page,
      alternative.snippet,
    );
    this.recount();
  }

  toggleAlternatives(row: AiFieldRow) {
    row.showAlternatives = !row.showAlternatives;
  }

  private sameValue(a: any, b: any): boolean {
    if (!hasValue(a) && !hasValue(b) && typeof a !== "boolean") return true;
    return sameControlValue(a, b);
  }

  toggleItem(row: AiItemRow) {
    this.validateItem(row);
    row.accepted = !row.accepted && !row.blocked;
    this.recount();
  }

  onItemEdited(row: AiItemRow) {
    this.validateItem(row);
    if (row.blocked) row.accepted = false;
    this.recount();
  }

  onDivisionChosen(row: AiItemRow) {
    const option = (this.lookups?.divisions ?? []).find(
      (entry) => Number(entry.id) === Number(row.data.divisionId),
    );
    row.data.divisionLabel = option?.label ?? null;
    this.onItemEdited(row);
  }

  onCharacteristicChosen(row: AiItemRow) {
    const option = (this.lookups?.characteristics ?? []).find(
      (entry) => Number(entry.id) === Number(row.data.characteristicId),
    );
    row.data.groupId = option?.groupId ?? null;
    row.data.catalogLabel = option?.label ?? null;
    this.onItemEdited(row);
  }

  onNearbyChosen(row: AiItemRow) {
    const option = (this.lookups?.nearby ?? []).find(
      (entry) => Number(entry.id) === Number(row.data.nearbyId),
    );
    row.data.nearbyLabel = option?.label ?? null;
    if (!hasValue(row.data.unitId)) {
      row.data.unitId = this.lookups?.units?.[0]?.id ?? null;
    }
    this.onItemEdited(row);
  }

  chooseContact(row: AiItemRow, contactId: number | null) {
    row.data.selectedContactId = contactId;
    this.onItemEdited(row);
    if (!row.blocked) row.accepted = true;
    this.recount();
  }

  onDocumentChanged(document: AiDocumentRow) {
    document.dirty = true;
  }

  requestRetry(document: AiDocumentRow) {
    document.processing = true;
    document.failed = false;
    this.retryDocument.emit(document.id);
  }

  // -------------------------------------------------------------- evidência

  showFieldEvidence(row: { item: ExtractionItem }) {
    this.showEvidence(
      `${row.item.id}`,
      row.item.documentId,
      row.item.page,
      row.item.snippet,
    );
  }

  showDocument(document: AiDocumentRow) {
    this.showEvidence(`doc-${document.id}`, document.id, 1, null);
  }

  private showEvidence(
    key: string,
    documentId: number | null,
    page: number | null,
    snippet: string | null,
  ) {
    this.selectedKey = key;
    this.viewerPage = page ?? 1;
    this.viewerSnippet = snippet;
    const runId = this.result?.run?.id;
    const document = documentId ? this.documentsById.get(documentId) : null;
    if (!runId || !document) {
      this.viewerSource = null;
      return;
    }
    if (this.viewerSource?.documentId === document.id) return;

    const cached = this.files.get(document.id);
    if (cached) {
      this.viewerSource = this.toSource(document, cached);
      return;
    }

    this.fileSub?.unsubscribe();
    this.viewerLoading = true;
    this.fileSub = this.extraction.getDocumentFile(runId, document.id).subscribe({
      next: (bytes) => {
        this.files.set(document.id, bytes);
        this.viewerSource = this.toSource(document, bytes);
        this.viewerLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.viewerSource = null;
        this.viewerLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private toSource(document: ExtractionDocument, bytes: ArrayBuffer): AiViewerSource {
    return {
      documentId: document.id,
      fileName: document.fileName,
      mimeType: document.mimeType,
      bytes,
    };
  }

  // ------------------------------------------------------------ confirmação

  private fieldFinalValue(row: AiFieldRow): any {
    let value = row.value;
    if (row.editor === "date") {
      const date = toDate(value);
      value = date
        ? `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`
        : null;
    }
    const option = row.options.find((entry) => Number(entry.id) === Number(value));
    return {
      value,
      label: option?.label ?? row.item.payload?.catalogLabel ?? null,
    };
  }

  confirm() {
    const result = this.result;
    if (!result || this.saving) return;

    const fields: AiReviewConfirmEvent["decisions"]["fields"] = [];
    const items: ExtractionItem[] = [];

    const fieldRows = this.sections
      .reduce((list, section) => list.concat(section.rows), [] as AiFieldRow[])
      .concat(this.extras);

    for (const row of fieldRows) {
      const decision: ExtractionDecision =
        row.item.kind === "EXTRA"
          ? "PENDING"
          : row.accepted
          ? row.edited
            ? "EDITED"
            : "ACCEPTED"
          : "REJECTED";
      const finalValue = row.accepted ? this.fieldFinalValue(row) : null;
      fields.push({ id: row.item.id, decision, finalValue });
      items.push({ ...row.item, decision, finalValue });
    }

    const itemRows = this.divisions
      .concat(this.characteristics)
      .concat(this.proximities)
      .concat(this.owners);
    for (const row of itemRows) {
      const changed = JSON.stringify(row.data) !== JSON.stringify(row.item.payload ?? {});
      const decision: ExtractionDecision = row.accepted
        ? changed
          ? "EDITED"
          : "ACCEPTED"
        : "REJECTED";
      const finalValue = row.accepted ? row.data : null;
      fields.push({ id: row.item.id, decision, finalValue });
      items.push({ ...row.item, decision, finalValue });
    }

    const documents = this.documents
      .filter((document) => document.dirty)
      .map((document) => ({
        id: document.id,
        propertyDocumentTypeId: document.propertyDocumentTypeId ?? null,
        attachToProperty: document.attachToProperty,
      }));

    const patch = buildFormPatch(
      result.run.id,
      items,
      result.run.documents,
      result.run.summary?.location ?? null,
    );

    this.confirmed.emit({ decisions: { fields, documents }, patch });
  }
}
